import useCommonFormStyles from "@/hooks/useCommonStyles";
import useCustomTranslation from "@/hooks/useTranslation";
import {
	convertTimestampToCurrentTimezone,
	dateFormat,
	east8ToEast9,
	formatNumberWithCommas,
	handleBrowserUrl,
	partiallyHideHash,
	pxToRem,
	setDataToClipboard,
} from "@/util/common";
import { Button, Input, Image, Space, Skeleton } from "antd-mobile";
import React, { useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { DownFill } from "antd-mobile-icons";
import CoinGamingPopup from "./CoinGaming";
import { CoinGamingData, TabType, WalletTabKeys } from "..";
import useWithdraw from "@/hooks/useWithdraw";
import SelectorPopup from "@/components/selectorPopup";
import { isEmpty, isNumber } from "lodash";
import { isDef } from "@/util/validate";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/redux/store";
import {
	getApiFinanceCashAddressMethod,
	postApiFinanceBalance,
} from "@/redux/reducers/authSlice";
import CustomEmpty from "@/components/Empty";
import useRouter from "@/hooks/useRouter";

const WithdrawalModule: React.FC<{
	onChange: (newValue: string, active?: TabType) => void; // 添加 active 参数
}> = ({ onChange }) => {
	const classes = useStyles();
	const commonFormStyles = useCommonFormStyles();
	const dispatch: AppDispatch = useDispatch();
	const { t } = useCustomTranslation();
	const { params: routerParams } = useRouter();
	const percentages = [5, 25, 50, 100];
	const { dataCashOrder } = useSelector((state: RootState) => state.auth);

	const [visiblePopup, setVisiblePopup] = useState(false);
	const [address, setAddress] = useState<string>("");
	const [remark, setRemark] = useState<string | null>();
	const [amount, setAmount] = useState<string | null>(null);
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [coinGamingData, setCoinGamingData] = useState<CoinGamingData>(null);
	const isButtonClick = useRef(false);

	const fetchData = () => {
		dispatch(
			getApiFinanceCashAddressMethod({
				page: 0,
				pageSize: 6,
				itemType: routerParams.type || 1,
			})
		);
		dispatch(postApiFinanceBalance({}));
	};
	const {
		visible,
		setVisible,
		payChannel,
		payChannelData,
		submitWithdraw,
		withdrawParams,
		switchPayChannel,
	} = useWithdraw(1);

	const getCurrentTime = () => {
		const now = convertTimestampToCurrentTimezone(new Date().getTime());
		const year = now.getFullYear();
		const month = String(now.getMonth() + 1).padStart(2, "0"); // 月份从0开始
		const day = String(now.getDate()).padStart(2, "0");
		const hours = String(now.getHours()).padStart(2, "0");
		const minutes = String(now.getMinutes()).padStart(2, "0");
		const seconds = String(now.getSeconds()).padStart(2, "0");

		return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
	};

	useEffect(() => {
		if (isEmpty(amount) || !isDef(payChannel) || isEmpty(address)) {
			setErrorMsg(null);
			return;
		}
		let amountFloat = parseFloat(amount!);
		if (!isNumber(amountFloat)) {
			setErrorMsg(t("IncorrectInputFormat"));
			return;
		}

		if (
			isDef(withdrawParams?.max) &&
			isNumber(+withdrawParams.max) &&
			amountFloat > +withdrawParams.max
		) {
			setErrorMsg(`${t("AmountShouldLess")} ${withdrawParams.max}`);
			return;
		}

		if (
			isDef(withdrawParams?.min) &&
			isNumber(+withdrawParams.min) &&
			amountFloat < +withdrawParams.min
		) {
			setErrorMsg(`${t("AmountShouldMore")} ${withdrawParams.min}`);
			return;
		}

		if (
			isDef(withdrawParams.balance) &&
			isNumber(withdrawParams.balance) &&
			amountFloat > withdrawParams.balance
		) {
			setErrorMsg(t("NotSufficientFunds"));
			return;
		}

		if (address?.length <= 28) {
			setErrorMsg(t("WarnAddressCheck"));
			return;
		}

		setErrorMsg(null);

		setCoinGamingData({
			currency: amount
				? (amountFloat - (withdrawParams.feeRate ?? 0)).toString()
				: null,
			feeRate: withdrawParams.feeRate,
			address: address,
			orderId: "",
			time: getCurrentTime(),
			type: "AccessRecords",
			logo: payChannel.logo,
			network: payChannel.network,
		});
	}, [amount, payChannel, address]);

	const handlePercentage = (percentage: number) => {
		const calculatedAmount =
			(withdrawParams.balance ?? 0) * (percentage / 100);
		setAmount(calculatedAmount.toFixed(4));
	};

	const resetFields = () => {
		setRemark(null);
		setAmount(null);
		setErrorMsg(null);
		setAddress("");
	};

	const onSubmit = async () => {
		if (!isDef(amount)) {
			return;
		}
		await submitWithdraw({
			remark,
			amount: parseFloat(amount),
			address: address,
		});
		resetFields();
		fetchData();
	};

	const getOrderStatusMessage = (status: number) => {
		if (status === 1) {
			return t("Approved");
		} else if (status === -1) {
			return t("Refunded");
		}
		return t("Processing");
	};

	const handleCoinGamingState = (data?: any) => {
		if (!isEmpty(data)) {
			const coinGamingData: any = {
				currency: (data.coin - data.fee).toString(),
				feeRate: data.fee,
				address: data.address,
				orderId: data.orderId,
				time: data.updateTime,
				type: "AccessRecords",
				logo: data.networkLogo,
				network: data.network,
				status: data.status,
				reason: data.reason,
			};
			if (data.status === 1) {
				coinGamingData.uri = handleBrowserUrl(
					data.network,
					data.transNo
				);
			}
			setCoinGamingData(coinGamingData);
			isButtonClick.current = false;
		} else {
			isButtonClick.current = true;
		}
		setVisiblePopup(true);
	};

	useEffect(() => {
		fetchData();
	}, []);

	return (
		<div className="router-view">
			<div className={commonFormStyles.formWrap}>
				<div className={commonFormStyles.labelTitle}>
					{t("SelectCurrency")}
				</div>
				<div className={commonFormStyles.inputWrap}>
					{!isEmpty(payChannel) ? (
						<Image
							src={payChannel.currencyLogo}
							className="icon"
							fallback={
								<Image
									src={require("@/assets/images/icons/USDT.png")}
									className="icon"
								/>
							}
						/>
					) : (
						<Skeleton animated className="icon" />
					)}
					{!isEmpty(payChannel) ? (
						<div className={commonFormStyles.selector}>
							<span>{payChannel.currency}</span>
						</div>
					) : (
						<Skeleton animated style={{ height: "80%" }} />
					)}
				</div>

				<div className={commonFormStyles.labelTitle}>
					{t("SelectNetwork")}
				</div>
				<div
					onClick={() => setVisible(true)}
					className={commonFormStyles.inputWrap}
				>
					{!isEmpty(payChannel) ? (
						<Image
							src={payChannel.logo}
							className="icon"
							fallback={
								<Image
									src={require("@/assets/images/icons/TRON.png")}
									className="icon"
								/>
							}
						/>
					) : (
						<Skeleton animated className="icon" />
					)}
					{!isEmpty(payChannel) ? (
						<div className={commonFormStyles.selector}>
							<span>{payChannel.network}</span>
							<DownFill color="#5E71B7" fontSize={10} />
						</div>
					) : (
						<Skeleton animated style={{ height: "80%" }} />
					)}
				</div>

				<dl className={classes.tipsWrap}>
					<dt>
						<Image
							src={require("@/assets/images/icons/wait.png")}
							className="icon"
						/>
						<span>{t("WarnTitle1")}</span>
					</dt>
					<dd>{t("WarnContent")}</dd>
				</dl>

				<div className={commonFormStyles.labelTitle}>
					{t("WithdrawalAddress")}
				</div>
				<div className={commonFormStyles.inputWrap}>
					<Input
						className={commonFormStyles.input}
						type="text"
						value={address ?? ""}
						placeholder={t("WithdrawalAddressTip")}
						onChange={(val) => setAddress(val)}
					/>
				</div>

				<div className={commonFormStyles.labelTitle}>
					{t("WithdrawalAmount")}
					<div className="remaining">
						<span>{t("AmountSvailableWithdrawal")}：</span>
						<strong>{withdrawParams.balance ?? 0} USDT</strong>
					</div>
				</div>
				<div className={commonFormStyles.inputWrap}>
					<Image
						src={require("@/assets/images/icons/USDT.png")}
						className="icon"
					/>
					<Input
						className={commonFormStyles.input}
						type="number"
						value={amount ?? ""}
						placeholder={t("WithdrawalAmountTip")}
						onChange={(val) => setAmount(val)}
					/>
				</div>

				<div className={commonFormStyles.labelTitle}>
					{t("Remark")} ({t("Optional")})
				</div>
				<div className={commonFormStyles.inputWrap}>
					<Input
						className={commonFormStyles.input}
						type="text"
						value={remark ?? ""}
						placeholder={t("RemarkTip")}
						onChange={(val) => setRemark(val)}
					/>
				</div>

				<div className={classes.withdrawalWrap}>
					<div className="title">
						<span>{t("MinimumWithdrawalAmount")}：</span>
						<strong>{payChannel?.minMoney ?? 0} USDT</strong>
					</div>
					<Space wrap>
						{percentages.map((percentage) => (
							<Button
								onClick={() => handlePercentage(percentage)}
								key={percentage}
								className="btn"
							>
								{percentage}%
							</Button>
						))}
					</Space>
				</div>
				<p style={{ color: "#FF7878" }}>
					<em>{errorMsg}</em>
				</p>
				<Button
					onClick={() => {
						handleCoinGamingState();
					}}
					block
					shape="rounded"
					className={commonFormStyles.submit}
					disabled={
						!isEmpty(errorMsg) ||
						isEmpty(amount) ||
						isEmpty(address)
					}
				>
					<Space
						style={{
							"--gap": pxToRem(6),
						}}
					>
						<Image
							src={require("@/assets/images/icons/arrow-circle-broken-down.png")}
							className="icon"
						/>
						<span>{t("btnWithdrawal")}</span>
					</Space>
				</Button>
			</div>
			<div className={classes.columnTitle}>
				<div className="l">
					<strong>{t("AccessRecords")}</strong>
				</div>
				<div
					onClick={() => {
						onChange(WalletTabKeys.ANIMALS, "AccessRecords");
					}}
					className="more"
				>
					{t("ViewAll")} {">>"}
				</div>
			</div>

			{dataCashOrder.length > 0 ? (
				<ul className={classes.listWrap}>
					{dataCashOrder.map((item: any) => (
						<li
							onClick={() => handleCoinGamingState(item)}
							key={item.id}
						>
							<div className="up">
								<Image
									src={require("@/assets/images/icons/USDT.png")}
									className="icon"
								/>
								<p
									onClick={() => {
										setDataToClipboard(
											item.address,
											t("CopySuccess")
										);
									}}
								>
									{partiallyHideHash(item.address)}
								</p>
								<strong className="lose">
									-{formatNumberWithCommas(+item.coin)}
								</strong>
							</div>
							<div className="down">
								<span>
									{dateFormat(
										east8ToEast9(item.updateTime).getTime(),
										"YYYY-MM-dd hh:mm:ss"
									)}
								</span>
								<span>
									{getOrderStatusMessage(item.status)}
								</span>
							</div>
						</li>
					))}
				</ul>
			) : (
				<CustomEmpty />
			)}

			<CoinGamingPopup
				visible={visiblePopup}
				data={coinGamingData}
				onClose={() => setVisiblePopup(false)}
				onSubmit={() => {
					if (isButtonClick.current) {
						onSubmit();
					} else {
						setVisiblePopup(false);
					}
				}}
			/>

			<SelectorPopup
				visible={visible}
				data={payChannelData}
				onClose={() => setVisible(false)}
				onSelect={switchPayChannel}
			/>
		</div>
	);
};

export default WithdrawalModule;

const useStyles = createUseStyles({
	tipsWrap: {
		borderTop: "1px solid #2F3A62",
		padding: {
			top: pxToRem(20),
		},
		fontSize: pxToRem(14),
		fontWeight: 500,
		fontFamily: "Noto Sans SC",
		margin: {
			top: pxToRem(28),
			bottom: pxToRem(16),
		},
		"& dt": {
			display: "flex",
			alignItems: "center",
			color: "#FFFFFF",
			paddingBottom: pxToRem(18),
			"& .icon": {
				width: pxToRem(20),
				height: pxToRem(20),
				marginRight: pxToRem(8),
			},
		},
		"& dd": {
			color: "#868FA8",
			lineHeight: pxToRem(22),
		},
	},
	columnTitle: {
		display: "flex",
		justifyContent: "space-between",
		padding: {
			left: pxToRem(20),
			right: pxToRem(20),
			top: pxToRem(15),
			bottom: pxToRem(15),
		},
		"& .l": {
			flex: 1,
			overflow: "hidden",
			display: "flex",
			alignItems: "center",
			"& strong": {
				color: "#FFFFFF",
				fontFamily: "Noto Sans SC",
				fontSize: pxToRem(14),
				fontWeight: 500,
			},
		},
		"& .more": {
			color: "#868FA8",
			fontSize: pxToRem(14),
			fontWeight: 400,
		},
	},

	listWrap: {
		"& li": {
			borderTop: "1px solid #2F3A62",
			padding: {
				top: pxToRem(16),
				left: pxToRem(20),
				right: pxToRem(22),
				bottom: pxToRem(20),
			},
			"& >div": {
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
			},
		},
		"& .up": {
			fontWeight: 500,
			marginBottom: pxToRem(3),
			"& .icon": {
				width: pxToRem(24),
				height: pxToRem(24),
				marginRight: pxToRem(3),
			},
			"& p": {
				flex: 1,
				overflow: "hidden",
				color: "#FFFFFF",
				fontSize: pxToRem(14),
			},
			"& strong": {
				color: "#8BFF78",
				fontSize: pxToRem(16),
			},
		},
		"& .down": {
			color: "#868FA8",
			fontWeight: 400,
			fontSize: pxToRem(12),
		},
	},
	withdrawalWrap: {
		paddingBottom: pxToRem(10),
		"& .title": {
			color: "#FFFFFF",
			fontFamily: "Noto Sans SC",
			fontWeight: 500,
			fontSize: pxToRem(12),
			padding: {
				top: pxToRem(8),
				bottom: pxToRem(10),
			},
			"& span": {
				color: "#868FA8",
			},
		},
		"& .btn": {
			width: pxToRem(76),
			height: pxToRem(34),
			fontSize: pxToRem(14),
			"--border-width": "none",
			"--text-color": "#868FA8",
			"--background-color": "#151821",
			"&:active": {
				opacity: 0.8,
			},
		},
	},
});
